import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatBRL } from '@/lib/formatters';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Send, MessageCircle, Loader2, Calculator } from 'lucide-react';

const formatarMes = (mesReferencia) => {
  const [ano, mes] = mesReferencia.split('-');
  const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${nomes[parseInt(mes, 10) - 1]}/${ano}`;
};

const ContadorDetalhe = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const [contador, setContador] = useState(null);
  const [notasCompra, setNotasCompra] = useState([]);
  const [notasVenda, setNotasVenda] = useState([]);
  const [notasServico, setNotasServico] = useState([]);
  const [notasTomadas, setNotasTomadas] = useState([]);
  const [outrosDocumentos, setOutrosDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enviandoMes, setEnviandoMes] = useState(null);
  const [mesesEnviados, setMesesEnviados] = useState({});

  useEffect(() => {
    if (company?.id) {
      fetchDados();
    }
  }, [company?.id, id]);

  const fetchDados = async () => {
    try {
      setLoading(true);
      const [contadoresRes, notasCompraRes, notasVendaRes, notasServicoRes, notasTomadasRes, outrosDocumentosRes] = await Promise.all([
        axiosInstance.get(`/contadores/${company.id}`),
        axiosInstance.get(`/notas-fiscais`, { params: { empresa_id: company.id, contador_id: id } }),
        axiosInstance.get(`/notas-venda`, { params: { empresa_id: company.id, contador_id: id } }),
        axiosInstance.get(`/notas-servico`, { params: { empresa_id: company.id, contador_id: id } }),
        axiosInstance.get(`/notas-tomadas`, { params: { empresa_id: company.id, contador_id: id } }),
        axiosInstance.get(`/outros-documentos`, { params: { empresa_id: company.id, contador_id: id } }),
      ]);
      const encontrado = contadoresRes.data.find((c) => c.id === id);
      setContador(encontrado || null);
      setNotasCompra(notasCompraRes.data);
      setNotasVenda(notasVendaRes.data);
      setNotasServico(notasServicoRes.data);
      setNotasTomadas(notasTomadasRes.data);
      setOutrosDocumentos(outrosDocumentosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do contador');
    } finally {
      setLoading(false);
    }
  };

  const agruparPorMes = (lista) => lista.reduce((acc, nota) => {
    acc[nota.mes_referencia] = acc[nota.mes_referencia] || [];
    acc[nota.mes_referencia].push(nota);
    return acc;
  }, {});

  const gruposCompra = agruparPorMes(notasCompra);
  const gruposVenda = agruparPorMes(notasVenda);
  const gruposServico = agruparPorMes(notasServico);
  const gruposTomada = agruparPorMes(notasTomadas);
  const gruposOutros = agruparPorMes(outrosDocumentos);
  const mesesOrdenados = Array.from(new Set([...Object.keys(gruposCompra), ...Object.keys(gruposVenda), ...Object.keys(gruposServico), ...Object.keys(gruposTomada), ...Object.keys(gruposOutros)])).sort().reverse();

  const handleEnviarMes = async (mesReferencia) => {
    try {
      setEnviandoMes(mesReferencia);
      const response = await axiosInstance.post('/notas-fiscais/enviar-mes', {
        contador_id: id,
        mes_referencia: mesReferencia,
      });
      toast.success(`${response.data.atualizadas} nota(s) marcada(s) como enviada(s)`);
      setMesesEnviados((prev) => ({ ...prev, [mesReferencia]: response.data.contador }));
      fetchDados();
    } catch (error) {
      toast.error('Erro ao enviar notas do mês');
    } finally {
      setEnviandoMes(null);
    }
  };

  const handleEnviarWhatsApp = (mesReferencia, dadosContador) => {
    const portalUrl = `${window.location.origin}/portal-contador/${dadosContador.portal_token}`;
    const mensagem = `Olá ${dadosContador.nome}! Seguem as notas fiscais de ${formatarMes(mesReferencia)} para envio à contabilidade. Acesse o portal para baixar os arquivos: ${portalUrl}`;
    window.open(`https://wa.me/${dadosContador.whatsapp}?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  if (loading && !contador) {
    return (
      <div className="flex min-h-screen bg-zinc-950 text-white">
        <Sidebar user={user} onLogout={onLogout} activePage="contadores" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="contadores" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/contadores')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
          </div>

          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Calculator className="w-8 h-8 text-orange-400" />
              {contador?.nome || 'Contador'}
            </h1>
            <p className="text-zinc-400 mt-1">{contador?.email} · {contador?.whatsapp}</p>
          </div>

          {mesesOrdenados.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-8 text-center text-zinc-500">
                Nenhuma nota lançada para este contador
              </CardContent>
            </Card>
          ) : (
            mesesOrdenados.map((mes) => {
              const compraDoMes = gruposCompra[mes] || [];
              const vendaDoMes = gruposVenda[mes] || [];
              const servicoDoMes = gruposServico[mes] || [];
              const tomadaDoMes = gruposTomada[mes] || [];
              const outrosDoMes = gruposOutros[mes] || [];
              const temPendente = [...compraDoMes, ...vendaDoMes, ...servicoDoMes, ...tomadaDoMes, ...outrosDoMes].some((n) => n.status === 'pendente');
              const contadorEnvio = mesesEnviados[mes] || (!temPendente ? contador : null);

              return (
                <Card key={mes} className="bg-zinc-900 border-zinc-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{formatarMes(mes)}</CardTitle>
                    <div className="flex gap-2">
                      {temPendente && (
                        <Button
                          size="sm"
                          onClick={() => handleEnviarMes(mes)}
                          disabled={enviandoMes === mes}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          {enviandoMes === mes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Enviar notas do mês
                        </Button>
                      )}
                      {!temPendente && contadorEnvio && (
                        <Button
                          size="sm"
                          onClick={() => handleEnviarWhatsApp(mes, contadorEnvio)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Enviar via WhatsApp
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-400 mb-2">Notas de Compra</h3>
                      {compraDoMes.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nenhuma nota de compra neste mês</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Empresa</TableHead>
                              <TableHead>Fornecedor</TableHead>
                              <TableHead>Data NF</TableHead>
                              <TableHead>Valor</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {compraDoMes.map((nota) => (
                              <TableRow key={nota.id} className="border-zinc-800">
                                <TableCell className="capitalize">{nota.unidade}</TableCell>
                                <TableCell>{nota.fornecedor_nome}</TableCell>
                                <TableCell>{nota.data_nf}</TableCell>
                                <TableCell>{formatBRL(nota.valor)}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={nota.status === 'enviada'
                                      ? 'border-green-500/50 text-green-400'
                                      : 'border-amber-500/50 text-amber-400'
                                    }
                                  >
                                    {nota.status === 'enviada' ? 'Enviada' : 'Pendente'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-zinc-400 mb-2">Notas de Venda</h3>
                      {vendaDoMes.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nenhuma nota de venda neste mês</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Empresa</TableHead>
                              <TableHead>Arquivo</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendaDoMes.map((nota) => (
                              <TableRow key={nota.id} className="border-zinc-800">
                                <TableCell className="capitalize">{nota.unidade}</TableCell>
                                <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={nota.status === 'enviada'
                                      ? 'border-green-500/50 text-green-400'
                                      : 'border-amber-500/50 text-amber-400'
                                    }
                                  >
                                    {nota.status === 'enviada' ? 'Enviada' : 'Pendente'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-zinc-400 mb-2">NFs Prestado</h3>
                      {servicoDoMes.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nenhuma NF Prestado neste mês</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Empresa</TableHead>
                              <TableHead>Arquivo</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {servicoDoMes.map((nota) => (
                              <TableRow key={nota.id} className="border-zinc-800">
                                <TableCell className="capitalize">{nota.unidade}</TableCell>
                                <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={nota.status === 'enviada'
                                      ? 'border-green-500/50 text-green-400'
                                      : 'border-amber-500/50 text-amber-400'
                                    }
                                  >
                                    {nota.status === 'enviada' ? 'Enviada' : 'Pendente'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-zinc-400 mb-2">NFs Tomado</h3>
                      {tomadaDoMes.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nenhuma NF Tomado neste mês</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Empresa</TableHead>
                              <TableHead>Arquivo</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tomadaDoMes.map((nota) => (
                              <TableRow key={nota.id} className="border-zinc-800">
                                <TableCell className="capitalize">{nota.unidade}</TableCell>
                                <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={nota.status === 'enviada'
                                      ? 'border-green-500/50 text-green-400'
                                      : 'border-amber-500/50 text-amber-400'
                                    }
                                  >
                                    {nota.status === 'enviada' ? 'Enviada' : 'Pendente'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-zinc-400 mb-2">Outros Documentos</h3>
                      {outrosDoMes.length === 0 ? (
                        <p className="text-sm text-zinc-500">Nenhum documento neste mês</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-zinc-800">
                              <TableHead>Empresa</TableHead>
                              <TableHead>Arquivo</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {outrosDoMes.map((documento) => (
                              <TableRow key={documento.id} className="border-zinc-800">
                                <TableCell className="capitalize">{documento.unidade}</TableCell>
                                <TableCell>{documento.arquivo_pdf_nome}</TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={documento.status === 'enviada'
                                      ? 'border-green-500/50 text-green-400'
                                      : 'border-amber-500/50 text-amber-400'
                                    }
                                  >
                                    {documento.status === 'enviada' ? 'Enviada' : 'Pendente'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ContadorDetalhe;
